import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { MemberSidebarLayout } from './SidebarLayout';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { User, Mail, Phone, Calendar, Camera, Save, Loader2, Lock, Shield, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { ClickableImage } from './ImagePreview';
import { PhoneInput } from './PhoneInput';
import { ReuploadIdDocsCard } from './ReuploadIdDocsCard';
import { ImageWithFallback } from './ui/image-with-fallback';
import { AddressFieldsAutosuggest } from './auth/AddressFieldsAutosuggest';
import { CircularImageCropModal } from './CircularImageCropModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function MemberProfilePage() {
  const { user, api } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);
  const [showPwdForm, setShowPwdForm] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [showOldPwd, setShowOldPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [cropOpen, setCropOpen] = useState(false);
  const [pendingPhotoFile, setPendingPhotoFile] = useState(null);
  const photoRef = useRef(null);

  // Light fields saved via PUT /api/auth/profile
  const [form, setForm] = useState({
    mobile: '',
    bio: '',
  });
  // Structured address saved via POST /api/auth/me/address
  // (same shape as the merchant registration form — single source of truth).
  const [address, setAddress] = useState({
    address_line: '',
    city: '',
    state: '',
    country: '',
    country_code: '',
    state_id: null,
    city_id: null,
    postal_code: '',
  });

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const { data } = await api.get('/api/auth/me');
        setProfile(data);
        setForm({
          mobile: data.mobile || '',
          bio: data.bio || '',
        });
        // Prefer structured `address` saved during registration; gracefully
        // fall back to legacy flat fields for accounts created before the
        // structured schema was introduced.
        const a = (data.address && typeof data.address === 'object') ? data.address : {};
        setAddress({
          address_line: a.address_line || (typeof data.address === 'string' ? data.address : '') || '',
          city: a.city || data.city || '',
          state: a.state || data.province || '',
          country: a.country || '',
          country_code: a.country_code || '',
          state_id: a.state_id ?? null,
          city_id: a.city_id ?? null,
          postal_code: a.postal_code || data.zip_code || '',
        });
      } catch { toast.error('Failed to load profile'); }
      setLoading(false);
    };
    loadProfile();
  }, [api]);

  const handleSave = async () => {
    // Validate required address fields (mirrors registration requirements).
    const required = [
      ['address_line', 'Street address'],
      ['city', 'City'],
      ['state', 'State / Province'],
      ['country', 'Country'],
    ];
    for (const [key, label] of required) {
      if (!String(address[key] || '').trim()) {
        toast.error(`${label} is required`);
        return;
      }
    }
    if (!address.country_code) {
      toast.error('Please pick a country from the suggestions');
      return;
    }

    setSaving(true);
    try {
      // 1) Persist the structured address (also re-runs geocoding).
      await api.post('/api/auth/me/address', {
        address_line: address.address_line.trim(),
        city: address.city.trim(),
        state: address.state.trim(),
        country: address.country.trim(),
        country_code: address.country_code,
        state_id: address.state_id ?? null,
        city_id: address.city_id ?? null,
        postal_code: (address.postal_code || '').trim() || null,
      });
      // 2) Persist mobile + bio via the existing profile endpoint.
      const { data } = await api.put('/api/auth/profile', {
        mobile: form.mobile,
        bio: form.bio,
      });
      toast.success(data.message || 'Profile updated');
      // Refresh user state so the read-only header reflects new values.
      try {
        const { data: me } = await api.get('/api/auth/me');
        setProfile(me);
      } catch { /* ignored */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    }
    setSaving(false);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file.');
      return;
    }
    // Open the crop modal — the upload happens after the user confirms.
    setPendingPhotoFile(file);
    setCropOpen(true);
    if (photoRef.current) photoRef.current.value = '';
  };

  const handlePhotoCropConfirm = async (croppedBlob) => {
    if (!croppedBlob) return;
    try {
      const fd = new FormData();
      const croppedFile = new File([croppedBlob], 'profile.png', { type: 'image/png' });
      fd.append('file', croppedFile);
      fd.append('purpose', 'profile-pic');
      const uploadRes = await api.post('/api/upload/member-file', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = uploadRes.data.url || uploadRes.data.file_url;
      await api.put('/api/auth/profile', { profile_photo_url: url });
      setProfile(prev => ({ ...prev, profile_photo_url: url }));
      toast.success('Photo updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally {
      setPendingPhotoFile(null);
    }
  };

  const handlePasswordChange = async () => {
    if (pwdForm.new_password.length < 6) { toast.error('Minimum 6 characters'); return; }
    if (pwdForm.new_password !== pwdForm.confirm) { toast.error('Passwords do not match'); return; }
    setChangingPwd(true);
    try {
      await api.put('/api/auth/profile/password', {
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password,
      });
      toast.success('Password changed');
      setShowPwdForm(false);
      setPwdForm({ current_password: '', new_password: '', confirm: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setChangingPwd(false);
  };

  if (loading) return (
    <MemberSidebarLayout>
      <div className="flex items-center justify-center min-h-[50vh]"><Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" /></div>
    </MemberSidebarLayout>
  );

  const photoUrl = profile?.profile_photo_url || null;

  return (
    <MemberSidebarLayout>
      <div className="p-6 lg:p-8 max-w-3xl mx-auto space-y-6" data-testid="member-profile-page">
        <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-white">My Profile</h1>

        {/* Profile Header */}
        <Card className="bg-[#041A3B] border-white/10">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row items-center gap-6">
              {/* Avatar */}
              <div className="relative group">
                <div className="w-24 h-24 rounded-full overflow-hidden bg-[#000B18] border-2 border-[#00E5FF]/30 flex items-center justify-center">
                  {photoUrl ? (
                    <ImageWithFallback
                      src={photoUrl}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      fallback={<span className="text-[#00E5FF] text-3xl font-bold">{profile?.name?.[0] || 'U'}</span>}
                    />
                  ) : (
                    <span className="text-[#00E5FF] text-3xl font-bold">{profile?.name?.[0] || 'U'}</span>
                  )}
                </div>
                <button onClick={() => photoRef.current?.click()}
                  className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-testid="upload-photo-btn">
                  <Camera className="w-6 h-6 text-white" />
                </button>
                <input ref={photoRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" />
              </div>

              <div className="text-center sm:text-left flex-1">
                <div className="flex items-center gap-2 justify-center sm:justify-start">
                  <h2 className="text-white text-xl font-semibold">{profile?.name}</h2>
                  {profile?.verification_status === 'verified' && (
                    <Badge className="bg-[#00E676]/20 text-[#00E676] text-xs"><Shield className="w-3 h-3 mr-1" />Verified</Badge>
                  )}
                </div>
                <p className="text-[#A0B4CC] text-sm mt-1">{profile?.email}</p>
                <div className="flex gap-2 mt-2 justify-center sm:justify-start">
                  <Badge className={profile?.subscription_status === 'active' ? 'bg-[#00E676]/20 text-[#00E676]' : 'bg-[#5C7599]/20 text-[#5C7599]'}>
                    {profile?.subscription_status === 'active' ? `Active - ${profile?.subscription_plan || 'Member'}` : 'Not Subscribed'}
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Locked Fields */}
        <Card className="bg-[#041A3B] border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-white font-outfit text-lg flex items-center gap-2"><Lock className="w-4 h-4 text-[#5C7599]" /> Account Info (Read-only)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[#A0B4CC] text-xs flex items-center gap-1"><User className="w-3 h-3" /> Full Name</Label>
                <div className="mt-1 px-3 py-2 bg-[#000B18] rounded-md border border-white/10 text-white/60 text-sm">{profile?.name}</div>
              </div>
              <div>
                <Label className="text-[#A0B4CC] text-xs flex items-center gap-1"><Mail className="w-3 h-3" /> Email</Label>
                <div className="mt-1 px-3 py-2 bg-[#000B18] rounded-md border border-white/10 text-white/60 text-sm">{profile?.email}</div>
              </div>
              <div>
                <Label className="text-[#A0B4CC] text-xs flex items-center gap-1"><Calendar className="w-3 h-3" /> Birthdate</Label>
                <div className="mt-1 px-3 py-2 bg-[#000B18] rounded-md border border-white/10 text-white/60 text-sm">{profile?.birthdate || 'Not set'}</div>
              </div>
              <div>
                <Label className="text-[#A0B4CC] text-xs">Referral Code</Label>
                <div className="mt-1 px-3 py-2 bg-[#000B18] rounded-md border border-white/10 text-[#00E5FF] text-sm font-mono">{profile?.referral_code}</div>
              </div>
            </div>

            {/* ID photos */}
            <div className="pt-2">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[#A0B4CC] text-xs">Uploaded Documents</Label>
                <ReuploadIdDocsCard
                  api={api}
                  currentIdUrl={profile?.id_photo_url}
                  currentSelfie={profile?.selfie_url}
                  onUpdated={async () => {
                    // Refresh profile + verification badge after reupload
                    try {
                      const { data } = await api.get('/api/auth/me');
                      setProfile(data);
                    } catch { /* ignore */ }
                  }}
                />
              </div>
              {(profile?.id_photo_url || profile?.selfie_url) ? (
                <div className="flex gap-4">
                  {profile?.id_photo_url && (
                    <div className="flex-1">
                      <p className="text-[#5C7599] text-xs mb-1">Government ID</p>
                      <ClickableImage src={profile.id_photo_url} label="Government ID"
                        alt="ID" className="w-full h-24 object-cover rounded-lg border border-white/10" />
                    </div>
                  )}
                  {profile?.selfie_url && (
                    <div className="flex-1">
                      <p className="text-[#5C7599] text-xs mb-1">Selfie</p>
                      <ClickableImage src={profile.selfie_url} label="Selfie"
                        alt="Selfie" className="w-full h-24 object-cover rounded-lg border border-white/10" />
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-[#5C7599] text-xs italic">No ID documents on file. Use Re-upload to add them.</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Editable Fields */}
        <Card className="bg-[#041A3B] border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-white font-outfit text-lg">Edit Profile</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label className="text-[#A0B4CC] text-xs flex items-center gap-1"><Phone className="w-3 h-3" /> Mobile</Label>
              <div className="mt-1 max-w-md">
                <PhoneInput
                  value={form.mobile}
                  onChange={(v) => setForm({ ...form, mobile: v })}
                  data-testid="profile-mobile"
                />
              </div>
            </div>

            {/* Structured address — auto-suggest country / state / city. */}
            <AddressFieldsAutosuggest
              value={address}
              onChange={setAddress}
              required={true}
            />

            <div>
              <Label className="text-[#A0B4CC] text-xs">Bio</Label>
              <Textarea value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} placeholder="Tell us about yourself..." rows={3}
                className="mt-1 bg-[#000B18] border-white/20 text-white" data-testid="profile-bio" />
            </div>
            <Button onClick={handleSave} disabled={saving} className="btn-primary" data-testid="save-profile-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </CardContent>
        </Card>

        {/* Password Change */}
        <Card className="bg-[#041A3B] border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="text-white font-outfit text-lg flex items-center gap-2"><Lock className="w-4 h-4" /> Change Password</CardTitle>
          </CardHeader>
          <CardContent>
            {!showPwdForm ? (
              <Button onClick={() => setShowPwdForm(true)} variant="ghost" className="text-[#00E5FF]" data-testid="change-pwd-btn">
                Change Password
              </Button>
            ) : (
              <div className="space-y-4 max-w-sm">
                <div>
                  <Label className="text-[#A0B4CC] text-xs">Current Password</Label>
                  <div className="relative mt-1">
                    <Input type={showOldPwd ? 'text' : 'password'} value={pwdForm.current_password}
                      onChange={e => setPwdForm({ ...pwdForm, current_password: e.target.value })}
                      className="bg-[#000B18] border-white/20 text-white pr-10" data-testid="current-pwd" />
                    <button onClick={() => setShowOldPwd(!showOldPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C7599]">
                      {showOldPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-[#A0B4CC] text-xs">New Password</Label>
                  <div className="relative mt-1">
                    <Input type={showNewPwd ? 'text' : 'password'} value={pwdForm.new_password}
                      onChange={e => setPwdForm({ ...pwdForm, new_password: e.target.value })}
                      className="bg-[#000B18] border-white/20 text-white pr-10" data-testid="new-pwd" />
                    <button onClick={() => setShowNewPwd(!showNewPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C7599]">
                      {showNewPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label className="text-[#A0B4CC] text-xs">Confirm New Password</Label>
                  <Input type="password" value={pwdForm.confirm} onChange={e => setPwdForm({ ...pwdForm, confirm: e.target.value })}
                    className="mt-1 bg-[#000B18] border-white/20 text-white" data-testid="confirm-new-pwd" />
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => setShowPwdForm(false)} variant="ghost" className="text-[#A0B4CC]">Cancel</Button>
                  <Button onClick={handlePasswordChange} disabled={changingPwd} className="btn-primary" data-testid="save-pwd-btn">
                    {changingPwd ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Change Password'}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        <CircularImageCropModal
          open={cropOpen}
          onOpenChange={setCropOpen}
          file={pendingPhotoFile}
          onConfirm={handlePhotoCropConfirm}
          outputSize={512}
          title="Crop your profile photo"
        />
      </div>
    </MemberSidebarLayout>
  );
}
